import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const ColombiaPage = () => {
  const colombiaData = {
    name: 'Colombia',
    region: 'Latin America',
    color: '#708080',
    metaTitle: 'Pharma & Nutraceutical Registration in Colombia | INVIMA | CTD Dossiers & Supplements',
    metaDescription: 'Explore registration pathways for pharmaceutical and nutraceutical products in Colombia. INVIMA\'s CTD-aligned requirements, health-product classification, key documents and strategic export opportunity in Latin America.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Colombia',
      subtitle: 'One of Latin America\'s fastest-growing healthcare markets with increasing regulatory alignment (CTD structure) and a growing pipeline of opportunities for chronic therapy and wellness supplements.',
      cta: 'Start Your Colombia Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~52 million' },
      { label: 'Health Insurance Coverage', value: '~95% population covered' },
      { label: 'Regulatory Agency', value: 'INVIMA' },
      { label: 'Regulatory Trend', value: '2025 initiative to expedite 14,000+ pending registrations' },
      { label: 'Registration Timeline', value: '10-14 months (typical)' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Growth Market with Regulatory Momentum',
      stats: [
        'Emerging as promising pharma and consumer health market within Latin America',
        'Registration process for medicines and OTCs typically 10-14 months',
        'INVIMA launched Resolution 2025010547 (March 2025) to accelerate registrations and reduce backlog',
        'Regulatory authority recognises dossiers from reference jurisdictions',
        'Investing in digital tools for faster workflow',
        'Evolving framework for nutraceuticals/health-products with stronger pharmacovigilance expectations'
      ],
      opportunities: [
        'Large-scale market (~52M population) with high insurance coverage (95%)',
        'Strong regulatory momentum with recent acceleration initiatives',
        'CTD alignment enables efficient dossier reuse from other markets',
        'Strategic LatAm gateway for regional expansion'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: Rising NCDs & Wellness Demand',
      subtitle: 'Colombia shows increasing burden of non-communicable diseases with growing demand for chronic therapies and wellness products.',
      categories: [
        {
          name: 'Non-Communicable Diseases',
          items: [
            'Increasing burden of NCDs with growing demand for chronic-therapies and generics',
            'Rising obesity/overweight trend following Latin America regional patterns',
            'Large insured population (95%) drives demand for cardio-metabolic treatments',
            'Diabetes and hypertension prevalence growing',
            'Pharma demand: Antidiabetics, antihypertensives, lipid-lowering agents',
            'Pharma demand: Generics and biologics increasingly relevant',
            'Biosimilar guidelines established creating opportunities for biosimilars'
          ]
        },
        {
          name: 'Wellness & Prevention Market',
          items: [
            'Growing wellness supplement market for cardiovascular health',
            'Increasing demand for metabolic support products',
            'Vitamins/minerals and immune support gaining traction',
            'Must ensure classification, labelling and claims comply with Colombian regulations',
            'Nutra opportunities: Cardiovascular health, metabolic support, immunity',
            'Nutra opportunities: High-quality vitamins/minerals for insured population',
            'Premium positioning possible given insurance coverage'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'INVIMA - Instituto Nacional de Vigilancia de Medicamentos y Alimentos',
      description: 'INVIMA is the national regulator for medicines, biologicals, food, health products and their surveillance. Colombia uses CTD format for dossier submissions. Regulations for biologicals and biosimilars issued via Decree 1782/2014 and Resolutions 2950/2019. Strong pharmacovigilance systems mandated - new rules from May 2025 require local Contact Person for PV, PSMF in Spanish and submission of ICSRs.',
      pharma: {
        timeline: '10-14 months typical for OTC/pharma registration under favourable conditions. With 2025 Contingency Plan, processing may accelerate. Registrations have validity periods requiring renewal.',
        requirements: [
          'Foreign manufacturers must appoint authorised local representative or importer registered with INVIMA',
          'Documento Técnico Común (CTD) format mandatory for medicines and biologics',
          'Digital submission increasingly expected',
          'Administrative application form and legal documents',
          'CTD dossier (Modules 1-5): Module 1 customised for Colombia, Modules 2-5 per ICH/CTD standards',
          'GMP certificate for manufacturing site',
          'Clinical/non-clinical data as needed (especially for new/biologic products)',
          'Label and packaging artwork in Spanish',
          'Pharmacovigilance requirements: Local PV Contact Person (LPPV) from May 2025',
          'PSMF in Spanish required',
          'ICSRs: serious within 15 days, non-serious within 90 days'
        ],
        insight: 'Colombia mandates CTD format making dossier reuse efficient. Recent 2025 Resolution aims to accelerate 14,000+ pending registrations. Typical timelines 10-14 months but may improve with new initiatives. Strong PV requirements from May 2025 including local responsible person and Spanish documentation.'
      },
      nutra: {
        timeline: 'Varies based on classification. Supplements requiring health registration need import licences, COAs, local representation. Must ensure correct classification to avoid pharmaceutical route.',
        requirements: [
          'Colombia distinguishes medicinal products, health-supplements/alimentos funcionales, medical devices',
          'Correct classification critical: dosage form, nutrient/active levels, claims determine treatment as medicine or supplement',
          'Supplements requiring health registration need import licences',
          'COAs and local representation required',
          'Labelling in Spanish mandatory',
          'Detailed composition documentation',
          'Advertising and claims regulated - disease-treatment claims not allowed unless re-classified as medicines',
          'Classification submission may be required',
          'Import licence and/or sanitary permit for imported products',
          'Proof of compliance with food/regulation requirements for supplements'
        ],
        insight: 'Classification is key determinant of regulatory pathway. Disease-treatment claims push product into medicinal category requiring full pharmaceutical registration. Supplements with appropriate wellness/nutritional claims can use simpler pathway but still require local representation, import licence and Spanish labelling.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with INVIMA (CTD)',
      steps: [
        'Local Representative / MAH - Foreign manufacturers must appoint authorised local representative or importer registered with INVIMA',
        'Dossier Format - CTD mandatory for medicines and biologics; digital submission increasingly expected',
        'Application Package: Administrative application form, legal documents',
        'CTD dossier (Modules 1-5): Module 1 customised for Colombia',
        'Modules 2-5 per ICH/CTD standards (Quality, Safety, Efficacy)',
        'GMP certificate for manufacturing site',
        'Clinical/non-clinical data (especially for new/biologic products)',
        'Label and packaging artwork in Spanish',
        'Manufacturing/Import authorisation documents',
        'Assessment: 10-14 months typical under favourable conditions',
        '2025 Contingency Plan may accelerate processing for many dossiers',
        'Pharmacovigilance Setup: Appoint local PV Contact Person (LPPV)',
        'Maintain PSMF in Spanish',
        'Submit ICSRs (serious within 15 days, non-serious within 90 days)',
        'Renewal & Variations per INVIMA guidelines'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceuticals & Health Products in Colombia',
      steps: [
        'Classification Determination: Colombia distinguishes medicines, health-supplements/alimentos funcionales, devices',
        'Classification factors: dosage form, nutrient/active levels, claims (nutritional vs therapeutic)',
        'Correct classification critical to avoid pharmaceutical registration pathway',
        'Classification submission if required',
        'Import licence and/or sanitary permit for imported products',
        'Composition sheet with active and excipient details',
        'Finished-product COA',
        'Labelling in Spanish with compliant claims',
        'Local representation documentation',
        'Proof of compliance with food/regulation requirements',
        'Advertising materials must avoid disease-treatment claims',
        'Health-support and nutritional claims acceptable if properly substantiated',
        'Monitor regulatory evolution as framework strengthens'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (INVIMA CTD)': [
        'Application form (INVIMA authorised format)',
        'CTD dossier in digital form',
        'Module 1: Colombia-specific administrative/legal docs',
        'Modules 2-5: Quality, Safety, Efficacy per ICH standards',
        'GMP certificate of manufacturing site',
        'Manufacturing/Import authorisation documents',
        'Local MAH representation documentation',
        'Label/pack design in Spanish',
        'PV documentation: Local Contact Person, PSMF in Spanish'
      ],
      'Nutraceuticals / Health Products': [
        'Classification submission (if required)',
        'Import licence and/or sanitary permit',
        'Composition sheet with full formulation',
        'Finished-product COA',
        'Labelling in Spanish with compliant claims',
        'Proof of compliance with food/supplement regulations',
        'Local representative documentation'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Colombia Should Feature in Your LatAm Strategy',
      reasons: [
        'Large population (~52M) and growing healthcare market',
        'High insurance coverage (~95%) ensuring broad access to medicines and supplements',
        'Regulatory momentum: 2025 initiatives to accelerate approvals and increase clarity',
        'CTD alignment makes dossier reuse from other markets efficient',
        'Strategic LatAm gateway: Success helps pave way to other Latin American markets',
        'Growing middle class with purchasing power for wellness products',
        'Established biosimilar guidelines creating opportunities'
      ],
      bestPortfolio: [
        'Pharma: Chronic-disease therapies (antidiabetics, antihypertensives, lipid-lowering agents), generics and biosimilars with proper CTD documentation',
        'Nutra: Metabolic support, cardiovascular health, weight-management supplements, vitamins/minerals with appropriate wellness/nutritional claims (not disease-treatment)'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Colombia',
      services: [
        'Portfolio & Market Fit - Identify SKUs matching Colombia\'s chronic-disease burden and regulatory opportunity',
        'CTD Dossier Adaptation - Convert existing dossiers to Colombia-compliant CTD (especially Module 1 Spanish-specific)',
        'Local MAH Setup - Manage local representative establishment and submission file preparation',
        'Supplement Strategy - Guide classification of nutraceuticals vs medicines, ensure labelling, claims, compositions meet Colombian rules',
        'Pharmacovigilance Setup - Establish local PV Contact Person, prepare Spanish PSMF, set up ICSR reporting systems',
        'Regulatory Roadmap - Map timelines, monitor 2025 Contingency Plan progress, support submission tracking',
        'LatAm Cluster Planning - Use Colombia dossier assets as base for entry into other LatAm markets'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines and supplements in Colombia?',
        answer: 'The National Institute for Drug and Food Surveillance (INVIMA) oversees medicines, biologicals, food, supplements and health-products.'
      },
      {
        question: 'Does Colombia require CTD format?',
        answer: 'Yes. Colombia uses the Documento Técnico Común (CTD) structure for medicines and biologicals registrations with digital submission increasingly expected.'
      },
      {
        question: 'How long does registration typically take?',
        answer: 'For many products, registration may take 10-14 months under favourable conditions. The 2025 Resolution aims to accelerate processing for pending applications.'
      },
      {
        question: 'How are supplements regulated?',
        answer: 'Supplements/health-products must comply with food/health product regulation. Classification is key - disease-treatment claims push a product into medicinal category requiring full pharmaceutical registration.'
      },
      {
        question: 'What are the new pharmacovigilance requirements?',
        answer: 'From May 2025, MAHs must appoint a local pharmacovigilance-responsible person (LPPV), maintain a PSMF in Spanish, submit ICSRs (serious within 15 days, non-serious within 90 days) and maintain risk-management plans (RMPs).'
      }
    ]
  };

  return <CountryTemplate countryData={colombiaData} />;
};
